// Service notifications API
import { getAPI, token } from "@/api/params.js";

// Time interval of updating requests (in milliseconds)
const UPDATE_INTERVAL = 5000;

// Notification objects dictionary (key is ID)
const notifications = {};
// It's true if notifications was received from server at least one time
let initialized = false;

// function will execute when notification added
let addCallback = null;
// function will execute when notification has read
let readCallback = null;
// function will execute when notification has removed
let removeCallback = null;
// function will execute when notifications fetched first time
let initCallback = null;
// function will execute when notification request has failed
let errorCallback = null;

/**
 * Set functions that will be execute when some notification information has updated
 * @param {Function} add New notification received (or load from API) handler, argument is notification data object
 * @param {Function} read Notification was read by user handler, argument is notification ID
 * @param {Function} remove Notification was removed handler, argument is notification ID
 * @param {Function} init Notifications load at first time handler
 * @param {Function} error Updating was failed handler, argument is error message
 */
export const subscribeToNotificationUpdates = (
  add,
  read,
  remove,
  init,
  error
) => {
  addCallback = add;
  readCallback = read;
  removeCallback = remove;
  initCallback = init;
  errorCallback = error;
  updateNotifications();
  setInterval(updateNotifications, UPDATE_INTERVAL);
};

/**
 * Remove notification by it's ID
 * @param {Number} id
 */
export const removeNotification = async id => {
  const removeURL = `${getAPI()}/notifications/${id}?token=${token}`;
  const resp = await fetch(removeURL, { method: "DELETE" });

  const { success, error } = await resp.json();

  if (!success) {
    throw new Error(error);
  }

  removeCallback(id);
};

/**
 * Remove all notifications
 */
export const removeAllNotifications = async () => {
  const removeURL = `${getAPI()}/notifications?token=${token}`;
  const resp = await fetch(removeURL, { method: "DELETE" });

  const { success, error } = await resp.json();

  if (!success) {
    throw new Error(error);
  }

  for (const id in notifications) {
    removeCallback(id);
  }
};

/**
 * Mark all unread notifications as read
 */
export const readNotifications = async () => {
  // Get only unread messages to reduce network traffic
  const ids = Object.entries(notifications)
    .filter(e => !e[1].isRead)
    .map(e => parseInt(e[0]));
  if (ids.length == 0) {
    return;
  }

  // request notifications reading
  await requestRead(ids);
  // call handler for read messages
  ids.forEach(id => readCallback(id));
};

/**
 * Request new notifications and check updates in it's state
 */
async function updateNotifications() {
  // Skip updating till API endpoint will be initialized
  if (getAPI() == undefined) {
    return;
  }

  try {
    const latestNotifs = await fetchNotifications();

    // update initialized status
    if (!initialized) {
      initialized = true;
      initCallback();
    }
    let hasNewNitif = true;
    // find new and read notifications
    for (let i = 0; i < latestNotifs.length; i++) {
      const notif = latestNotifs[i];

      if (notif.id in notifications) {
        if (notif.isRead != notifications[notif.id].isRead) {
          // if notification's read status changed, then call subscribed function
          readCallback(notif.id);
        }
      } else {
        handleNewNotification(notif, hasNewNitif);
        if(hasNewNitif){
          hasNewNitif = false
        }
      }
    }

    // find removed notifications
    for (var id in notifications) {
      const notifIdx = latestNotifs.findIndex(n => n.id == id);
      if (notifIdx < 0) {
        handleRemovedNotification(id);
      }
    }
  } catch (e) {
    errorCallback(e);
  }
}

/**
 * Save notification data and call handler
 * @param {Object} data Notification data
 */
function handleNewNotification(data, firstNotif) {
  // add new info
  notifications[data.id] = data;
  // call subscribed function
  addCallback(data, firstNotif);
}

/**
 * Remove notification data and call handler
 * @param {Number} id
 */
function handleRemovedNotification(id) {
  // remove notification
  delete notifications[id];
  // call subscribed function
  removeCallback(id);
}

/**
 * Request notifications list
 */
async function fetchNotifications() {
  const fetchURL = `${getAPI()}/notifications?token=${token}`;
  const resp = await fetch(fetchURL, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    }
  });

  const { items, success = true, error } = await resp.json();
  if (!success) {
    throw Error(error);
  }
  return items;
}

/**
 * Request notifications set to read
 * @param {Array} ids IDs of read notifications
 */
async function requestRead(ids) {
  const readURL = `${getAPI()}/notifications/setRead?token=${token}`;
  const body = JSON.stringify({
    ids: ids
  });

  const resp = await fetch(readURL, {
    method: "PUT",
    body: body,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    }
  });

  const { success, error } = await resp.json();
  if (!success) {
    throw new Error(error);
  }
}
